import cloneDeep from 'lodash.clonedeep'
import { TFunction } from 'next-i18next'
import { nanoid } from 'nanoid'

import {
  getAvatarImage,
  getImageByName,
  getSectionImage,
  initialValuesForCustomization,
} from '@web-apps/feature-creator-page'
import {
  Color,
  getDefaultImageFromAPIResponse,
  HTTP_CLIENT_REQUESTS_TIMEOUT_IN_MS,
  request,
} from '@web-apps/utils-shared'
import {
  BackgroundType,
  BackgroundTypeStyle,
  ButtonShapeStyle,
  ColorOpacity,
  CreatorPageType,
  CustomizationFormProperties,
  ExternalLinkMode,
  ImageNamesEnum,
  ImageResponseType,
  ProductRecommendationsSectionType,
  SectionCoverFormat,
  SectionExtension,
  SectionType,
  SectionTypeEnum,
  SectionVariantEnum,
  SocialLinkType,
} from '@web-apps/utils-types'
import {
  LINK_SERVICES,
  MergeAccountOptionsType,
  MergeAccountsType,
  NormalizedFetchedData,
  PreviewThemeType,
  ServicesSlugsMap,
} from './types'

import { findLinktreeSlug, isImageURLValid, proxyImageUrl } from './utils'

const SLUG_ALLOWED_CHARS_REG = /^[a-zA-Z0-9._]+$/
const FORMAT_ERROR = 'FORMAT_ERROR'
export const MAX_SECTIONS = 100
export const MAX_SOCIALS = 20

export const fetchLinkServiceData = async (
  serviceType: LINK_SERVICES,
  slug?: string
) => {
  if (!slug) return null

  try {
    if (!SLUG_ALLOWED_CHARS_REG.test(slug)) {
      return { error: FORMAT_ERROR, requestParams: { slug, serviceType } }
    }

    const { data } = await request().get(
      `/api/scraper/${serviceType}?slug=${slug}`,
      { timeout: HTTP_CLIENT_REQUESTS_TIMEOUT_IN_MS }
    )

    return data
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
  } catch (e: any) {
    throw {
      ...e,
      requestParams: {
        slug,
        serviceType,
      },
    }
  }
}

const normalizeSections = ({
  sectionsData,
  options,
}: {
  sectionsData: SectionType[]
  options: MergeAccountOptionsType
}) => {
  const sections = (sectionsData || []).filter(
    (item) =>
      !(
        (item.label || '').length > 100 ||
        (item.tagline || '').length > 100 ||
        (item._links?.about?.href || '').length > 1024
      )
  )

  if (sections.length > 0) {
    sections[0] = convertExternalLinkSectionToCover(sections[0])
  }

  const productRecommendationImagePlaceholder = [
    {
      href: 'https://media.zezam.io/static/themes/product-placeholder.svg',
      name: ImageNamesEnum.SECTION,
      type: 'image/svg+xml',
    },
  ]

  const productRecommendationPlaceholder = {
    type: SectionTypeEnum.EXTERNAL_LINK,
    styles: {
      variant: SectionVariantEnum.COVER,
    },
    published: true,
    mode: ExternalLinkMode.LINK,
    _links: {
      self: {
        id: '',
      },
      'self/image': productRecommendationImagePlaceholder,
      about: {
        href: 'https://zezam.io',
      },
    },
  }

  const productRecommendation = {
    type: SectionTypeEnum.CONTAINER,
    label: options.messages.productRecommendationsSection.title,
    styles: {
      variant: SectionVariantEnum.PLAIN,
      is_product_recommendations: true,
      extension: SectionExtension.NONE,
    },
    published: true,
    items: [
      {
        ...productRecommendationPlaceholder,
        ...{
          _links: {
            ...productRecommendationPlaceholder._links,
            ...{ self: { id: nanoid() } },
          },
        },
      },
      {
        ...productRecommendationPlaceholder,
        ...{
          _links: {
            ...productRecommendationPlaceholder._links,
            ...{ self: { id: nanoid() } },
          },
        },
      },
      {
        ...productRecommendationPlaceholder,
        ...{
          _links: {
            ...productRecommendationPlaceholder._links,
            ...{ self: { id: nanoid() } },
          },
        },
      },
      {
        ...productRecommendationPlaceholder,
        ...{
          _links: {
            ...productRecommendationPlaceholder._links,
            ...{ self: { id: nanoid() } },
          },
        },
      },
    ],
    _links: {
      self: {
        href: 'https://zezam.io',
        id: nanoid(),
      },
    },
  }

  return [
    productRecommendation,
    {
      ...productRecommendation,
      _links: {
        ...productRecommendation._links,
        'self/image': productRecommendationImagePlaceholder,
        self: {
          ...productRecommendation._links.self,
          id: nanoid(),
        },
      },
      styles: {
        cover: SectionCoverFormat.LANDSCAPE,
        variant: SectionVariantEnum.COVER,
        extension: SectionExtension.ACCORDION,
        is_product_recommendations: true,
      },
    },
    {
      ...productRecommendation,
      _links: {
        ...productRecommendation._links,
        'self/image': productRecommendationImagePlaceholder,
        self: {
          ...productRecommendation._links.self,
          id: nanoid(),
        },
      },
      styles: {
        cover: SectionCoverFormat.LANDSCAPE,
        variant: SectionVariantEnum.IMAGE,
        extension: SectionExtension.ACCORDION,
        is_product_recommendations: true,
      },
    },
    ...sections,
  ] as SectionType[]
}

const convertExternalLinkSectionToCover = (section: SectionType) => {
  const sectionCopy = cloneDeep(section)

  if (
    sectionCopy.type === SectionTypeEnum.EXTERNAL_LINK &&
    sectionCopy.mode === ExternalLinkMode.LINK &&
    getDefaultImageFromAPIResponse(sectionCopy._links['self/image'])
  ) {
    sectionCopy.styles.variant = SectionVariantEnum.COVER
  }

  return sectionCopy
}

export const normalizeFetchedData = (
  data: MergeAccountsType,
  options: MergeAccountOptionsType
): NormalizedFetchedData => {
  const instagram = data[LINK_SERVICES.instagram]?.profile
  const linktree = data[LINK_SERVICES.linktree]?.profile
  const colors =
    data[LINK_SERVICES.instagram]?.colors?.paletteColors ||
    data[LINK_SERVICES.linktree]?.colors?.paletteColors ||
    []
  const social_links = ((linktree || instagram)?.social_links || [])
    .filter((item: SocialLinkType) => item.handle.length < 256)
    .slice(0, MAX_SOCIALS)

  // Instagram image is preferred. This is a workaround when we don't get Instagram data
  const image =
    instagram?._links['self/image']?.find(
      (item: ImageResponseType) => item.name === ImageNamesEnum.DEFAULT
    ) ||
    linktree?._links['self/image']?.find(
      (item: ImageResponseType) => item.name === ImageNamesEnum.DEFAULT
    )

  const sections = normalizeSections({
    sectionsData: (linktree || instagram)?.sections,
    options,
  }).slice(0, MAX_SECTIONS)

  return {
    creatorPage: {
      social_links,
      sections,
      name: (instagram || linktree)?.name?.slice(0, 50),
      tagline: (linktree || instagram)?.tagline?.slice(0, 2000),
      styles: {
        ...initialValuesForCustomization,
        [CustomizationFormProperties.BACKGROUND]: '#333333',
        [CustomizationFormProperties.BACKGROUND_TYPE_STYLE]:
          BackgroundTypeStyle.FADED_DARK,
        [CustomizationFormProperties.BACKGROUND_TYPE]: BackgroundType.Solid,
        [CustomizationFormProperties.BUTTONS_BACKGROUND_OPACITY]:
          ColorOpacity['100%'],
        [CustomizationFormProperties.BUTTONS_SHAPE]: ButtonShapeStyle.ROUNDED,
        [CustomizationFormProperties.BUTTONS_BACKGROUND_COLOR]: '#FFFFFF',
        [CustomizationFormProperties.TEXT]: '#FFFFFF',
      },
      _links: {
        ...linktree?._links,
        ...instagram?._links,
        image,
      },
      settings: {
        show_classic_footer: false,
        show_sticky_gradient_footer: false,
      },
    },
    colors,
  }
}

export const fetchData = async (
  { instagram, linktree }: ServicesSlugsMap,
  t: TFunction
) => {
  try {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const data: any = {}

    const fetchedServicesResponse = await Promise.allSettled([
      fetchLinkServiceData(LINK_SERVICES.linktree, linktree),
      fetchLinkServiceData(LINK_SERVICES.instagram, instagram),
    ])

    fetchedServicesResponse.forEach((service, index) => {
      // For wrong format, we want to send a specific error
      if (
        service.status === 'fulfilled' &&
        service.value?.error === FORMAT_ERROR
      ) {
        throw {
          serviceType: service.value.requestParams.serviceType,
          message: t('common:errors.user_not_correct', {
            serviceType: service.value.requestParams.serviceType,
          }),
        }
      }

      // linktree is service number one, we want to skip sending errors to the
      // client side as there is no input to fix it (only available via URI parameters)
      if (service.status === 'rejected') {
        if (index > 0) {
          const reason = service.reason
          throw {
            message: t(
              reason?.response.status === 404
                ? 'common:errors.user_not_found'
                : 'common:errors.service_not_available',
              {
                serviceType: reason?.requestParams.serviceType,
              }
            ),
            serviceType: reason?.requestParams.serviceType,
          }
        }
      } else {
        if (service.value) {
          if (index === 0) data[LINK_SERVICES.linktree] = service.value
          if (index === 1) data[LINK_SERVICES.instagram] = service.value
        }
      }
    })

    if (!data[LINK_SERVICES.linktree] && data[LINK_SERVICES.instagram]) {
      try {
        const linktreeData = await fetchLinkServiceData(
          LINK_SERVICES.linktree,
          findLinktreeSlug(data[LINK_SERVICES.instagram])
        )

        if (linktreeData) data[LINK_SERVICES.linktree] = linktreeData
      } catch (e) {
        // keep silence since it is a side effect
      }
    }

    if (Object.keys(data).length === 0) {
      throw new Error()
    }

    return normalizeFetchedData(data, {
      messages: {
        productRecommendationsSection: {
          title: t('pages:get_started.product_recommendations_example.title'),
        },
      },
    })

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
  } catch (e: any) {
    if ('serviceType' in e) throw e

    if (e.response?.status === 404) {
      throw {
        serviceType: e.requestParams?.serviceType,
        message: t('common:errors.user_not_found', {
          serviceType: e.requestParams?.serviceType,
        }),
      }
    }

    throw { message: t('pages:get_started.try_again_message') }
  }
}

const serializeSections = (sectionsData: SectionType[]) => {
  return sectionsData.map((section) => {
    const serializeSection = (s: SectionType) => {
      const { label, mode, tagline, type, published, styles, _links } = s
      const imageLink = getSectionImage(s)?.href

      return {
        label,
        tagline,
        styles,
        type,
        ...(imageLink &&
          isImageURLValid(imageLink) && { image_source: imageLink }),
        published,
        href: _links?.about?.href,
        mode,
      }
    }

    const serializeGridItems = (
      items: ProductRecommendationsSectionType['items']
    ) =>
      items.map((i) => {
        const { mode, tagline, type, published, styles, _links } = i
        const imageLink = getSectionImage(i as SectionType)?.href

        return {
          tagline,
          styles,
          type,
          ...(imageLink &&
            isImageURLValid(imageLink) && { image_source: imageLink }),
          published,
          convert_to_affiliate: false,
          href: _links?.about?.href,
          mode,
        }
      })

    const baseSection = serializeSection(section)

    switch (section.type) {
      case SectionTypeEnum.CONTAINER:
        return {
          ...baseSection,
          items: serializeGridItems(section.items),
        }
      default: {
        return baseSection
      }
    }
  })
}

export const serializeData = (
  data: CreatorPageType,
  theme?: PreviewThemeType
) => {
  const avatarImageHref = getAvatarImage(data)?.href
  const profileImage = isImageURLValid(avatarImageHref)
    ? avatarImageHref
    : 'https://media.zezam.io/static/themes/profile.png'

  const images = {
    'profile-image': profileImage,
  }

  let styles = data.styles
  data.sections.reverse()

  if (theme) {
    const { themeName, avatarAsBackground, ...themeStyles } = theme
    styles = themeStyles
  }

  return {
    profile: {
      profile: {
        name: data.name,
        tagline: data.tagline,
      },
      social_links: data.social_links,
      sections: serializeSections(data.sections),
      styles,
      images,
    },
  }
}

export const updateCreatorPageDataForPreview = (
  data: CreatorPageType,
  colors: Color[],
  theme?: PreviewThemeType
): CreatorPageType => {
  const creatorPage = cloneDeep(data)

  if ('self/image' in creatorPage._links) {
    creatorPage._links['self/image'] = Array.isArray(
      creatorPage._links['self/image']
    )
      ? creatorPage._links['self/image'].map(
          (image: ImageResponseType & { needsProxy?: boolean }) =>
            image.needsProxy
              ? {
                  ...image,
                  href: proxyImageUrl(image.href),
                }
              : image
        )
      : undefined
  }

  for (const item of creatorPage.sections) {
    if ('self/image' in item._links) {
      item._links['self/image'] = Array.isArray(item._links['self/image'])
        ? item._links['self/image'].map(
            (image: ImageResponseType & { needsProxy?: boolean }) =>
              image.needsProxy
                ? {
                    ...image,
                    href: proxyImageUrl(image.href),
                  }
                : image
          )
        : undefined
    }
  }

  if (theme) {
    const avatarImage = getAvatarImage(creatorPage)
    const { themeName, avatarAsBackground, ...themeStyles } = theme
    creatorPage.styles = { ...themeStyles }

    if (avatarAsBackground && avatarImage) {
      creatorPage._links['self/image'] = [
        avatarImage,
        {
          ...avatarImage,
          name: ImageNamesEnum.BACKGROUND,
        },
      ]
    }
  }

  return creatorPage
}
